import { IDestroyOptions, Rectangle, Sprite, Text, TextStyle } from "pixi.js";
import { CommonResourcesPackage } from "by/gamefactory/cityversetycoon/scenes/common/resources/CommonResourcesPackage";
import { FadeScreen } from "by/gamefactory/cityversetycoon/scenes/game/components/FadeScreen";
import { BasePageView } from "../../../../../common/components/BasePageView";
import { HourlyBonusResourcePackage } from "../../resources/HourlyBonusResourcePackage";
import { SoundsResourcePackageWrapper } from "../../../../../common/resources/SoundsResourcePackageWrapper";
import { IAnalyticProviderWrapper } from "../../../../../../../analytics/IAnalyticProviderWrapper";
import { ICanvasScaler } from "../../../../../../../canvas/ICanvasScaler";
import { IFortuneWheelController } from "../../controller/IFortuneWheelController";
import { IFortuneWheelModel } from "../../../model/IFortuneWheelModel";
import { PromiseCompletionSource } from "../../../../../../../shared/async/PromiseCompletionSource";
import { Wheel } from "../../components/Wheel";
import { ButtonWithScaleAnimation } from "../../../../../common/components/ButtonWithScaleAnimation";
import { WheelErrorMessage } from "../../components/WheelErrorMessage";
import { InputBlocker } from "../../../../../game/components/InputBlocker";
import { VirtualScreenEvent } from "../../../../../../../analytics/events/VirtualScreenEvent";
import { GameFont } from "../../../../../common/GameFont";
import { SpinButton } from "../../components/SpinButton";
import { HourlyBonusPanel } from "../../components/hourlybonus/HourlyBonusPanel";
import { gsap } from "gsap";
import { ILogger } from "by/gamefactory/cityversetycoon/logger/ILogger";
import { WedgeRewardData } from "../../../model/WedgeRewardData";
import { WedgeRewardType } from "../../../model/WedgeRewardType";
import { WheelDiceRollsPage } from "./WheelDiceRollsPage";
import { IPlayerModel } from "../../../../../common/models/IPlayerModel";
import { TopPanel } from "../../../../../game/components/toppanel/TopPanel";
type Timeline = gsap.core.Timeline;

export class DailyWheelPage extends BasePageView {
    private static readonly WHEEL_ERROR_MESSAGE_TEXT = "IT SEEMS THAT WE'RE HAVING\nA TECHNICAL PROBLEM.\nYOU CAN STILL COME BACK LATER\n TO SPIN YOUR WHEEL FOR FREE";
    private static readonly BACK__HOURLY_TEXT = "HOURLY BONUS";

    private readonly commonResources: CommonResourcesPackage;
    private readonly wheelResources: HourlyBonusResourcePackage;
    private readonly soundResources: SoundsResourcePackageWrapper;
    private readonly analyticsProvider: IAnalyticProviderWrapper;
    private readonly playerModel: IPlayerModel;
    private readonly fortuneWheelModel: IFortuneWheelModel;
    private readonly fortuneWheelController: IFortuneWheelController;
    private readonly viewCompletionSource: PromiseCompletionSource<boolean>;
    private readonly topPanel: TopPanel;
    private readonly logger: ILogger;
    private readonly forceFreeWheel: boolean;
    private background: Sprite;
    private fadeScreen: FadeScreen;
    private giftWheel: Wheel;
    private spinButton: SpinButton;
    private lights: Array<Sprite>;
    private backButton: ButtonWithScaleAnimation;
    private headerText: Text;
    private wheelErrorContainer: WheelErrorMessage;
    private hourlyBonusPanel: HourlyBonusPanel;
    private InputBlocker: InputBlocker;
    private switchHourlyBonusToSpinAnimation: Timeline;

    public constructor(
        commonResources: CommonResourcesPackage,
        wheelResources: HourlyBonusResourcePackage,
        soundResources: SoundsResourcePackageWrapper,
        canvasScaler: ICanvasScaler,
        analyticsProvider: IAnalyticProviderWrapper,
        playerModel: IPlayerModel,
        fortuneWheelModel: IFortuneWheelModel,
        fortuneWheelController: IFortuneWheelController,
        viewCompletionSource: PromiseCompletionSource<boolean>,
        forceFreeWheel: boolean,
        topPanel: TopPanel,
        logger: ILogger
    ) {
        super(canvasScaler);
        this.commonResources = commonResources;
        this.wheelResources = wheelResources;
        this.soundResources = soundResources;
        this.analyticsProvider = analyticsProvider;
        this.playerModel = playerModel;
        this.fortuneWheelModel = fortuneWheelModel;
        this.fortuneWheelController = fortuneWheelController;
        this.viewCompletionSource = viewCompletionSource;
        this.topPanel = topPanel;
        this.logger = logger;
        this.switchHourlyBonusToSpinAnimation = new gsap.core.Timeline();
        this.forceFreeWheel = forceFreeWheel;
        this.lights = [];
        this.build();
        this.once("added", this.show);
    }

    public async start(): Promise<void> { }

    protected override build(): void {
        this.background = new Sprite(this.wheelResources.assets_views_hourly_bonus_wheel_popup_bg_png);
        this.background.eventMode = "static";
        this.background.anchor.set(0.5, 0.5);
        this.addChild(this.background);
        this.addChild(this.lightFactory());
        this.addChild(this.lightFactory());
        this.addChild(this.lightFactory());
        this.buildButtons();
        this.fadeScreen = new FadeScreen(0, 0, 0.8, this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.addChild(this.fadeScreen);
        this.giftWheel = new Wheel(this.wheelResources, this.commonResources, this.soundResources, this.fortuneWheelModel, this.logger);
        this.addChild(this.giftWheel);
        this.hourlyBonusPanel = new HourlyBonusPanel(this.commonResources, this.wheelResources, this.fortuneWheelModel, this.fortuneWheelController, this.analyticsProvider, this.canvasScaler);
        this.hourlyBonusPanel.onCompleteAllRewards.add(this.completeAllHourlyBonusRewardHandle.bind(this));
        this.addChild(this.hourlyBonusPanel);
        this.hourlyBonusPanel.onCollectDicesReward.add(this.hidePage, this);
        this.wheelErrorContainer = new WheelErrorMessage(this.commonResources, this.soundResources, DailyWheelPage.WHEEL_ERROR_MESSAGE_TEXT, this.hideError.bind(this));
        this.addChild(this.wheelErrorContainer);
        this.InputBlocker = new InputBlocker();
        this.addChild(this.InputBlocker);
        super.build();
    }

    private buildButtons(): void {
        this.backButton = new ButtonWithScaleAnimation(this.commonResources.assets_common_ui_back_png);
        this.backButton.hitArea = new Rectangle(-this.backButton.width * 0.5, -this.backButton.height, this.backButton.width * 2, this.backButton.height * 2);
        this.backButton.onUp.add(this.onExitClick, this);
        this.addChild(this.backButton);
        this.headerText = new Text(DailyWheelPage.BACK__HOURLY_TEXT, new BackTextStyle());
        this.headerText.anchor.set(0.5, 0.5);
        this.addChild(this.headerText);
        this.spinButton = new SpinButton(this.wheelResources, this.soundResources);
        this.spinButton.onSpinClicked.add(() => this.onSpinClick());
        this.addChild(this.spinButton);
    }

    private lightFactory(): Sprite {
        const light = new Sprite(this.wheelResources.assets_views_hourly_bonus_wheel_light_png);
        light.anchor.set(0.5, 0.5);
        this.lights.push(light);
        return light;
    }

    private async show(): Promise<void> {
        const animationTimeLine = new gsap.core.Timeline();
        this.InputBlocker.blocked = true;
        this.updateSpinAndHourlyBonusPanel();
        animationTimeLine.fromTo(this.giftWheel.scale, { x: 0, y: 0 }, { x: this.giftWheel.scale.x, y: this.giftWheel.scale.y, duration: 0.3, onStart: () => this.soundResources.assets_sounds_goldenwheel_new_wheel_wheel_and_text_appear_mp3.play() });
        animationTimeLine.fromTo(this.backButton, { x: this.canvasScaler.referenceWidth * 0.9, alpha: 0 }, { x: this.backButton.x, alpha: 1, delay: 0.45, duration: 0.3, ease: "power3.inout" });
        animationTimeLine.fromTo(this.headerText, { alpha: 0 }, { alpha: 1, duration: 0.1 }, "<80%");
        await animationTimeLine.play();
        animationTimeLine.kill();
        this.InputBlocker.blocked = false;
    }

    private updateSpinAndHourlyBonusPanel(): void {
        this.spinButton.visible = this.forceFreeWheel;
        this.hourlyBonusPanel.visible = !this.forceFreeWheel;
    }

    private async completeAllHourlyBonusRewardHandle(): Promise<void> {
        this.switchHourlyBonusToSpinAnimation.to(this.hourlyBonusPanel, { y: this.canvasScaler.referenceHeight + this.hourlyBonusPanel.height * 0.5, duration: 0.4 });
        this.switchHourlyBonusToSpinAnimation.set(this.hourlyBonusPanel, { visible: false });
        this.switchHourlyBonusToSpinAnimation.set(this.spinButton, { visible: true });
        this.switchHourlyBonusToSpinAnimation.fromTo(this.spinButton, { y: this.canvasScaler.referenceHeight + this.spinButton.height * 0.5 },
            {
                y: this.spinButton.y,
                duration: 0.4
            });
        await this.switchHourlyBonusToSpinAnimation.play();
        this.switchHourlyBonusToSpinAnimation.clear();
    }

    private async hidePage(): Promise<void> {
        this.InputBlocker.blocked = true;
        this.hourlyBonusPanel.killCollectAnimation();
        const hideAnimation = new gsap.core.Timeline();
        hideAnimation.to(this, { alpha: 0, duration: 0.3, ease: "power1.out" });
        await hideAnimation.play();
        hideAnimation.kill();
        this.viewCompletionSource.complete(true);
    }

    private async onSpinClick(): Promise<void> {
        this.InputBlocker.blocked = true;
        this.soundResources.assets_sounds_goldenwheel_new_wheel_free_spin_click_mp3.play();
        if (this.fortuneWheelModel.spinAvailableIn.value == undefined || this.fortuneWheelModel.spinAvailableIn.value == 0) {
            try {
                this.analyticsProvider.registerGoogleEvent(new VirtualScreenEvent("Spin wheel", this.analyticsProvider.currentEventIndex));
                const rewardData = await this.fortuneWheelController.claimHourlyBonusReward();
                await this.giftWheel.rotateWheel();
                this.topPanel.bringToOriginParent();
                await this.showWheelClaimResultPopup(rewardData);
                this.topPanel.bringToFront();
                this.fortuneWheelController.loadGoldenWheelStatus();
                await this.fortuneWheelController.updateFortuneWheelStatus();
                this.viewCompletionSource.complete(false);
            } catch (error: unknown) {
                this.logger.error(error);
                this.InputBlocker.blocked = false;
                this.topPanel.bringToFront();
            }
        } else {
            this.showDailyWheelError();
        }
        this.InputBlocker.blocked = false;
    }

    private async showWheelClaimResultPopup(rewardData: WedgeRewardData): Promise<void> {
        switch (rewardData.type) {
            case WedgeRewardType.DiceRoll:
                const diceRollsPageCS = new PromiseCompletionSource<void>();
                const wheelDiceRollsPage = new WheelDiceRollsPage(
                    this.commonResources,
                    this.wheelResources,
                    this.soundResources,
                    this.canvasScaler,
                    this.playerModel,
                    diceRollsPageCS,
                    rewardData.count
                );
                this.addChild(wheelDiceRollsPage);
                await diceRollsPageCS.result;
                this.removeChild(wheelDiceRollsPage);
                break;
            case WedgeRewardType.Coin:
            case WedgeRewardType.Shield:
            default:
                this.logger.warn(`Reward popup for type: ${rewardData.type} - not implemented.`);
                break;
        }
    }

    private async showDailyWheelError(): Promise<void> {
        const animationTimeLine = new gsap.core.Timeline();
        this.fadeScreen.eventMode = "static";
        this.fadeScreen.fadeIn();
        animationTimeLine.add(this.giftWheel.getWheelErrorAnimation());
        animationTimeLine.add(this.wheelErrorContainer.getShowErrorMessageAnimation());
        animationTimeLine.then(() => {
            animationTimeLine.kill();
        });
        animationTimeLine.play();
    }

    private async hideError(): Promise<void> {
        const animationTimeLine = new gsap.core.Timeline();
        this.soundResources.assets_sounds_goldenwheel_new_wheel_X_click_on_purchase_fail_mp3.play();
        animationTimeLine.addPause(0.3);
        await animationTimeLine.play();
        this.fadeScreen.eventMode = "none";
        this.fadeScreen.fadeOut();
        animationTimeLine.add(this.giftWheel.getHideErrorAnimation());
        animationTimeLine.add(this.wheelErrorContainer.getHideErrorMessageAnimation(), "<");
        animationTimeLine.then(() => {
            animationTimeLine.kill();
        });
    }

    private async onExitClick(): Promise<void> {
        this.soundResources.assets_sounds_upgrade_click_back_arrow_mp3.play();
        this.hidePage();
    }

    protected override layout(width: number, height: number): void {
        this.InputBlocker.width = width;
        this.InputBlocker.height = height;
        this.background.position.set(width * 0.5, height * 0.5);
        this.backButton.position.set(55, 235);
        this.headerText.position.set(278, 267);
        this.lightsLayout(width, height);
        this.spinButton.position.set(width * 0.5, height * 0.86);
        this.giftWheel.position.set(width * 0.5, 800);
        this.wheelErrorContainer.position.set(this.giftWheel.x, this.giftWheel.y + this.giftWheel.height * 0.5 + this.wheelErrorContainer.height * 0.5);
        this.hourlyBonusPanel.position = this.spinButton.position;
        super.layout(width, height);
    }

    private lightsLayout(width: number, height: number): void {
        const angle = 90;
        let boxIndex = 1;
        this.lights.forEach(light => {
            light.angle = boxIndex * angle;
            light.position.x = boxIndex === 0 ? width * 0.5 : boxIndex === 1 ? width * 0.09 : width * 0.91;
            light.position.y = boxIndex === 0 ? height - light.height * 0.5 : 800;
            boxIndex--;
        })
    }

    public destroy(options?: boolean | IDestroyOptions): void {
        this.spinButton.onSpinClicked.removeAll();
        this.backButton.onUp.remove(this.onExitClick, this);
        this.hourlyBonusPanel.onCompleteAllRewards.removeAll();
        this.hourlyBonusPanel.onCollectDicesReward.remove(this.hidePage, this);
        this.switchHourlyBonusToSpinAnimation.kill();
        super.destroy(options);
    }
}

class BackTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 60
        }));
    }
}
